import type { FlowSchedule, UpsertScheduleInput } from "../hooks/use-workflow-schedules"

import { pluginErrorCode } from "@/features/plugins/plugin-utils"

export type ScheduleFrequency = "interval" | "daily" | "weekly" | "advanced"

export type ScheduleFormState = {
  frequency: ScheduleFrequency
  intervalMinutes: number
  time: string
  weekday: string
  cron: string
}

const weekdayLabels: Record<string, string> = {
  "0": "Sunday",
  "1": "Monday",
  "2": "Tuesday",
  "3": "Wednesday",
  "4": "Thursday",
  "5": "Friday",
  "6": "Saturday",
}

function parseTime(time: string): { hour: number; minute: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim())
  if (!match) return null

  const hour = Number(match[1])
  const minute = Number(match[2])
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null
  return { hour, minute }
}

/** Returns the numeric value of a cron field, or null when it is not a plain number. */
function parseCronNumber(field: string, max: number): number | null {
  if (!/^\d{1,2}$/.test(field)) return null
  const value = Number(field)
  return value <= max ? value : null
}

type ScheduleConsentSettings = {
  default_prompt: string
  default_inputs: Record<string, unknown>
  notify: { emails?: string[]; on?: string[] }
}

export function scheduleConsentSettings(
  values: Partial<Pick<FlowSchedule, "default_prompt" | "default_inputs" | "notify">>
): ScheduleConsentSettings {
  return {
    default_prompt: values.default_prompt ?? "",
    default_inputs: values.default_inputs ?? {},
    notify: values.notify ?? {},
  }
}

export function scheduleCreateConsentSettings(
  defaultPrompt: string,
  notifyEmail: string
): ScheduleConsentSettings {
  return scheduleConsentSettings({
    default_prompt: defaultPrompt,
    default_inputs: {},
    notify: notifyEmail.trim() ? { emails: [notifyEmail.trim()], on: ["success", "failure"] } : {},
  })
}

export function scheduleActionError(error: unknown): string {
  return pluginErrorCode(error)
}

export function buildScheduleCreateInput(
  form: ScheduleFormState,
  options: {
    timezone: string
    defaultPrompt: string
    notifyEmail: string
  }
): UpsertScheduleInput {
  const settings = scheduleCreateConsentSettings(options.defaultPrompt, options.notifyEmail)
  const input: UpsertScheduleInput = {
    timezone: options.timezone,
    enabled: true,
    default_prompt: settings.default_prompt,
    default_inputs: settings.default_inputs,
    notify: settings.notify,
  }

  if (form.frequency === "interval") {
    input.interval_minutes = form.intervalMinutes
    input.cron = null
    return input
  }

  if (form.frequency === "advanced") {
    input.cron = form.cron.trim()
    input.interval_minutes = null
    return input
  }

  const parsed = parseTime(form.time)
  if (!parsed) {
    input.cron = ""
    input.interval_minutes = null
    return input
  }

  if (form.frequency === "weekly") {
    input.cron = `${parsed.minute} ${parsed.hour} * * ${form.weekday}`
    input.interval_minutes = null
    return input
  }

  input.cron = `${parsed.minute} ${parsed.hour} * * *`
  input.interval_minutes = null
  return input
}

export function getScheduleValidationError(form: ScheduleFormState): string | null {
  if (form.frequency === "interval") {
    return form.intervalMinutes > 0 ? null : "Choose an interval greater than zero."
  }

  if (form.frequency === "advanced") {
    return form.cron.trim() ? null : "A cron expression is required."
  }

  return parseTime(form.time) ? null : "Enter a valid time."
}

export function describeSchedule(
  schedule: Pick<FlowSchedule, "cron" | "interval_minutes">
): string {
  if (schedule.interval_minutes != null) {
    if (schedule.interval_minutes === 60) return "Every hour"
    if (schedule.interval_minutes % 60 === 0) {
      return `Every ${schedule.interval_minutes / 60} hours`
    }
    return `Every ${schedule.interval_minutes} minutes`
  }

  const cron = schedule.cron?.trim()
  if (!cron) return "Custom schedule"

  const parts = cron.split(/\s+/)
  if (parts.length === 5) {
    const [minuteField, hourField, dayOfMonth, month, weekday] = parts
    const minute = parseCronNumber(minuteField, 59)
    const hour = parseCronNumber(hourField, 23)
    // Only a plain "at this clock time" cron can be phrased as daily/weekly.
    // Steps, ranges and lists (e.g. "*/2") must keep their raw expression.
    if (minute != null && hour != null && dayOfMonth === "*" && month === "*") {
      const time = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
      if (weekday === "*") {
        return `Daily at ${time}`
      }
      if (weekdayLabels[weekday]) {
        return `Every ${weekdayLabels[weekday]} at ${time}`
      }
    }
  }

  return `Advanced cron: ${cron}`
}

export function buildScheduleToggleInput(
  schedule: Pick<FlowSchedule, "id" | "cron" | "interval_minutes">,
  enabled: boolean
): UpsertScheduleInput {
  const input: UpsertScheduleInput = {
    id: schedule.id,
    enabled,
  }

  if (schedule.cron) {
    input.cron = schedule.cron
  } else if (schedule.interval_minutes != null) {
    input.interval_minutes = schedule.interval_minutes
  }

  return input
}
